<template>
  <v-container fluid fill-height class="key-assignment-container">
    <!-- Hidden Scanner Input Field for Mobile Zebra Scanners -->
    <input
      ref="scannerInput"
      type="text"
      inputmode="none"
      :class="['scanner-input-field', { 'scanner-input-visible': showScannerInputDebug }]"
      @input="handleScannerInput"
      @blur="refocusScannerInput"
      autocomplete="off"
      placeholder="Scanner Input"
    />

    <!-- Scanning State (Default) -->
    <div v-if="!showUserList && !showError && !showSuccess && !loading" class="scan-idle-state">
      <div class="scan-icon-wrapper">
        <v-icon :size="120" color="warning" class="pulse-icon">mdi-key</v-icon>
      </div>

      <!-- Main Instruction -->
      <h2 class="scan-title">Scanne den Schlüssel des Benutzers</h2>

      <!-- Scanning indicator -->
      <div v-if="currentScanBuffer" class="scanning-indicator">
        <v-progress-circular indeterminate color="warning" size="32"></v-progress-circular>
        <span class="ml-3">Schlüssel wird gescannt...</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="scan-loading-state">
      <v-progress-circular indeterminate color="warning" size="80"></v-progress-circular>
      <h3 class="mt-6">{{ loadingMessage }}</h3>
    </div>

    <!-- Error State (Key already exists) -->
    <div v-if="showError && !loading" class="scan-error-state">
      <v-icon size="80" color="error" class="shake-icon">mdi-alert-circle</v-icon>
      <h3 class="error-title mt-4">Schlüssel bereits vergeben!</h3>
      <p class="error-message">
        Dieser Schlüssel ist bereits dem Benutzer
        <strong>{{ existingUser?.user }}</strong> zugewiesen.
      </p>
      <v-btn color="warning" @click="resetAll" class="mt-4" large>
        <v-icon left>mdi-restart</v-icon>
        Anderen Schlüssel scannen
      </v-btn>
    </div>

    <!-- User Selection List -->
    <div v-if="showUserList && !loading" class="user-selection-container">
      <div class="user-selection-header">
        <h3 class="selection-title">Zuweisen</h3>
      </div>

      <v-list class="user-list">
        <v-list-item
          v-for="user in users"
          :key="user.id"
          @click="selectUser(user)"
          class="user-item"
        >
          <v-list-item-avatar color="primary" size="48">
            <v-icon dark size="28">
              {{ user.role === 'admin' ? 'mdi-shield-account' : 'mdi-account' }}
            </v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="user-name">{{ user.user }}</v-list-item-title>
            <v-list-item-subtitle class="user-role">
              {{ user.role }}
              <span v-if="user.department"> | {{ user.department }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-icon color="grey lighten-1" size="28">mdi-chevron-right</v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <v-btn
        text
        block
        large
        class="mt-4 cancel-btn"
        @click="resetAll"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        Abbrechen
      </v-btn>
    </div>

    <!-- Success State -->
    <div v-if="showSuccess" class="scan-success-state">
      <v-icon size="80" color="success" class="pulse-icon">mdi-check-circle</v-icon>
      <h3 class="success-title mt-4">Erfolgreich zugewiesen!</h3>
      <p class="success-message">
        Der Schlüssel wurde dem Benutzer
        <strong>{{ assignedUser?.user }}</strong> zugewiesen.
      </p>
      <v-btn color="warning" @click="resetAll" class="mt-4" large>
        <v-icon left>mdi-refresh</v-icon>
        Weiteren Schlüssel zuweisen
      </v-btn>
    </div>

    <!-- Back Button (Bottom) - Only show when not in user selection or other states -->
    <div v-if="!showUserList && !showError && !showSuccess && !showConfirmModal" class="back-toggle">
      <v-btn text small color="grey" @click="goBack">
        <v-icon left small>mdi-arrow-left</v-icon>
        Zurück zum Menü
      </v-btn>
    </div>

    <!-- Confirmation Modal -->
    <v-dialog v-model="showConfirmModal" max-width="400" persistent>
      <v-card>
        <v-card-title class="warning white--text">
          <v-icon left color="white">mdi-key</v-icon>
          Schlüssel zuweisen?
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-center">
            <v-avatar size="60" color="primary" class="mb-3">
              <v-icon dark size="30">mdi-account</v-icon>
            </v-avatar>
            <h3 class="text-h6 mb-2">{{ selectedUser?.user }}</h3>
            <p class="text-body-2 grey--text mb-4">
              {{ selectedUser?.role }}
              <span v-if="selectedUser?.department"> | {{ selectedUser?.department }}</span>
            </p>
            <v-chip color="warning" outlined>
              <v-icon left small>mdi-key</v-icon>
              {{ scannedKey }}
            </v-chip>
            <p class="mt-4 text-body-1">
              Möchtest du diesen Schlüssel wirklich dem Benutzer zuweisen?
            </p>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            @click="showConfirmModal = false"
            :disabled="assigningKey"
          >
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            @click="confirmAssignment"
            :loading="assigningKey"
            :disabled="assigningKey"
          >
            <v-icon left>mdi-check-bold</v-icon>
            Bestätigen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import AuthService from '@/services/AuthService';

export default {
  name: 'KeyAssignment',
  data() {
    return {
      scannedKey: '',
      currentScanBuffer: '',
      scanTimeout: null,
      SCAN_TIMEOUT: 100,
      showScannerInputDebug: false, // Show scanner input field for debugging
      loading: false,
      loadingMessage: '',
      showError: false,
      showUserList: false,
      showSuccess: false,
      showConfirmModal: false,
      existingUser: null,
      users: [],
      selectedUser: null,
      assignedUser: null,
      assigningKey: false
    };
  },
  mounted() {
    // Add scanner keydown listener
    window.addEventListener('keydown', this.handleKeyPress);

    // Check for debug mode
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      this.showScannerInputDebug = true;
    }

    // Auto-focus the scanner input field
    if (this.$refs.scannerInput) {
      this.$nextTick(() => {
        this.$refs.scannerInput.focus();
      });
    }
  },
  beforeDestroy() {
    // Remove scanner keydown listener
    window.removeEventListener('keydown', this.handleKeyPress);

    // Clean up scan timeout
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }
  },
  methods: {
    handleKeyPress(event) {
      // Ignore if modal is open or showing results
      if (this.showError || this.showUserList || this.showSuccess || this.showConfirmModal) {
        return;
      }

      // Ignore if user is typing in an input field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      // Handle Enter key - process the scan
      if (event.key === 'Enter' && this.currentScanBuffer) {
        event.preventDefault();
        this.processScan();
        return;
      }

      // Handle printable characters for barcode/RFID scanning
      if (event.key.length === 1) {
        event.preventDefault();
        this.addToScanBuffer(event.key);
        return;
      }
    },

    addToScanBuffer(char) {
      // Clear any existing timeout
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }

      // Add character to buffer
      this.currentScanBuffer += char;

      // Set timeout to process scan if no more input comes
      this.scanTimeout = setTimeout(() => {
        if (this.currentScanBuffer) {
          this.processScan();
        }
      }, this.SCAN_TIMEOUT);
    },

    handleScannerInput(event) {
      const value = event.target.value;
      console.log('🔑 [KeyAssignment] Scanner input event:', value, 'Length:', value.length);

      // New Zebra scanners: Send complete key at once with Enter/newline
      if (value && (value.includes('\n') || value.includes('\r'))) {
        console.log('✅ [KeyAssignment] New scanner: Complete scan with newline detected');
        this.currentScanBuffer = value.trim();
        this.processScan();
        event.target.value = '';
        return;
      }

      // New Zebra scanners: Sometimes send complete key without newline
      // If we get a reasonable length string all at once, process it
      if (value && value.length >= 10) {
        console.log('✅ [KeyAssignment] New scanner: Complete scan detected (length >= 10)');
        this.currentScanBuffer = value.trim();
        this.processScan();
        event.target.value = '';
        return;
      }

      // Old scanners: Character by character - let keyboard handler accumulate
      console.log('🔄 [KeyAssignment] Old scanner: Letting keyboard handler accumulate characters');
    },

    refocusScannerInput() {
      if (this.$refs.scannerInput) {
        setTimeout(() => {
          this.$refs.scannerInput.focus();
        }, 100);
      }
    },

    async processScan() {
      console.log('🔍 Processing key scan:', this.currentScanBuffer);

      // Clear timeout
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
        this.scanTimeout = null;
      }

      const scannedKey = this.currentScanBuffer.trim();
      this.currentScanBuffer = '';

      if (!scannedKey) {
        console.error('❌ Empty scan buffer');
        return;
      }

      this.scannedKey = scannedKey;
      this.loading = true;
      this.loadingMessage = 'Schlüssel wird geprüft...';

      try {
        console.log('📡 Checking key:', scannedKey);

        // Step 1: Check if key already exists
        const checkResponse = await AuthService.getUserByKey(scannedKey);

        // Key exists - show error
        this.existingUser = checkResponse.data;
        this.showError = true;
        console.log('❌ Key already assigned to:', this.existingUser);
      } catch (error) {
        console.log('🔍 Error response:', error.response);
        console.log('🔍 Error status:', error.response?.status);
        console.log('🔍 Error data:', error.response?.data);

        if (error.response?.status === 404) {
          // Key not found - load users
          console.log('✅ Key available (404), loading users');
          await this.loadUsers();
          this.showUserList = true;
        } else if (error.response?.status === 403) {
          console.error('❌ 403 Forbidden - Admin access required');
          alert('Fehler: Admin-Berechtigung erforderlich. Bitte als Admin anmelden.');
        } else {
          console.error('❌ Error checking key:', error);
          console.error('❌ Full error object:', JSON.stringify(error, null, 2));
          const errorMsg = error.response?.data?.error || error.message || 'Unbekannter Fehler';
          alert(`Fehler beim Prüfen des Schlüssels: ${errorMsg}`);
        }
      } finally {
        this.loading = false;
        this.loadingMessage = '';
      }
    },

    async loadUsers() {
      try {
        console.log('📡 Loading users...');
        const response = await AuthService.getAllUsers();
        console.log('✅ Users loaded:', response.data.length, 'users');
        this.users = response.data;
      } catch (error) {
        console.error('❌ Error loading users:', error);
        console.error('❌ Error response:', error.response?.data);
        const errorMsg = error.response?.data?.error || error.message || 'Unbekannter Fehler';
        alert(`Fehler beim Laden der Benutzer: ${errorMsg}`);
        throw error;
      }
    },

    selectUser(user) {
      this.selectedUser = user;
      this.showConfirmModal = true;
    },

    async confirmAssignment() {
      if (!this.selectedUser || this.assigningKey) return;

      this.assigningKey = true;

      try {
        console.log('📡 Assigning key to user:', this.selectedUser.id);
        await AuthService.assignKeyToUser(this.selectedUser.id, this.scannedKey);

        // Success!
        console.log('✅ Key assigned successfully');
        this.assignedUser = this.selectedUser;
        this.showConfirmModal = false;
        this.showUserList = false;
        this.showSuccess = true;
      } catch (error) {
        console.error('❌ Error assigning key:', error);
        console.error('❌ Error response:', error.response?.data);
        const errorMsg = error.response?.data?.error || error.message || 'Unbekannter Fehler';
        alert(`Fehler beim Zuweisen des Schlüssels: ${errorMsg}`);
        this.showConfirmModal = false;
      } finally {
        this.assigningKey = false;
      }
    },

    resetAll() {
      this.scannedKey = '';
      this.currentScanBuffer = '';
      this.showError = false;
      this.showUserList = false;
      this.showSuccess = false;
      this.showConfirmModal = false;
      this.existingUser = null;
      this.users = [];
      this.selectedUser = null;
      this.assignedUser = null;
    },

    goBack() {
      this.$router.push('/warehouse/task-choice');
    }
  }
};
</script>

<style scoped>
/* Container */
.key-assignment-container {
  background: #ffffff !important;
  min-height: 100vh;
  height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0 !important;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Scan Idle State */
.scan-idle-state {
  text-align: center;
  color: #333;
  width: 100%;
  max-width: 400px;
  padding: 20px;
}

.scan-icon-wrapper {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.scan-title {
  font-size: 28px;
  font-weight: 600;
  color: #333 !important;
  margin: 0;
  line-height: 1.4;
}

.scanning-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
  color: #333;
  font-size: 18px;
  font-weight: 500;
  background: #f5f5f5;
  padding: 16px 24px;
  border-radius: 12px;
}

/* Loading State */
.scan-loading-state {
  text-align: center;
  color: #333;
}

.scan-loading-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

/* Error State */
.scan-error-state {
  text-align: center;
  color: #333;
  max-width: 400px;
  padding: 20px;
}

.shake-icon {
  animation: shake 0.5s;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

.error-title {
  font-size: 24px;
  font-weight: 700;
  color: #f44336;
}

.error-message {
  font-size: 18px;
  color: #333;
  margin-top: 12px;
  line-height: 1.4;
}

/* Success State */
.scan-success-state {
  text-align: center;
  color: #333;
  max-width: 400px;
  padding: 20px;
}

.success-title {
  font-size: 24px;
  font-weight: 700;
  color: #4CAF50;
}

.success-message {
  font-size: 18px;
  color: #333;
  margin-top: 12px;
  line-height: 1.4;
}

/* User Selection */
.user-selection-container {
  width: 100%;
  max-width: 500px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.user-selection-header {
  text-align: center;
  margin-bottom: 24px;
}

.selection-title {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.user-list {
  flex: 1;
  overflow-y: auto;
  border-radius: 12px;
  border: 2px solid #e0e0e0;
  background: white;
  max-height: 65vh;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  cursor: pointer;
  padding: 16px 20px !important;
  min-height: 72px;
}

.user-item:hover {
  background-color: rgba(255, 193, 7, 0.15);
  transform: translateX(2px);
}

.user-item:last-child {
  border-bottom: none;
}

.user-name {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #333 !important;
  margin-bottom: 4px !important;
}

.user-role {
  font-size: 14px !important;
  color: #666 !important;
}

.cancel-btn {
  font-size: 16px !important;
}

/* Back Toggle */
.back-toggle {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
}

/* Custom scrollbar */
.user-list::-webkit-scrollbar {
  width: 8px;
}

.user-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.user-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.user-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Responsive */
@media (max-width: 480px) {
  .scan-title {
    font-size: 22px;
  }

  .scanning-indicator {
    font-size: 16px;
    padding: 12px 20px;
    margin-top: 32px;
  }

  .error-title,
  .success-title {
    font-size: 22px;
  }

  .selection-title {
    font-size: 28px;
  }

  .error-message,
  .success-message {
    font-size: 16px;
  }

  .user-selection-container {
    padding: 16px;
  }

  .user-list {
    max-height: 55vh;
  }

  .user-item {
    padding: 14px 16px !important;
    min-height: 68px;
  }

  .user-name {
    font-size: 18px !important;
  }

  .user-role {
    font-size: 13px !important;
  }
}

/* Scanner Input Field - Hidden by default, visible in debug mode */
.scanner-input-field {
  position: fixed;
  top: -1000px;
  left: 10px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
  border: none;
  background: transparent;
  color: transparent;
  font-size: 16px;
  padding: 0;
}

.scanner-input-field.scanner-input-visible {
  /* Visible when debug=true in URL */
  position: fixed;
  top: 10px;
  left: 10px;
  right: 10px;
  width: calc(100% - 20px);
  height: 50px;
  opacity: 0.9;
  pointer-events: auto;
  z-index: 9999;
  border: 2px dashed #FFC107;
  background: #FFF9E1;
  color: #F57C00;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.scanner-input-field:focus {
  outline: none;
  border-color: #F57C00;
  background: #FFECB3;
}
</style>
